import { type FC, type ReactNode, useRef } from 'react';
import { type LucideIcon } from 'lucide-react';
import { kebabCase } from 'lodash';
import { Label, LabelVariants } from '@/components/ui/label';
import { Node } from '@/components/inspector/node';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type ActionKind = 'open-tree' | 'open-file';

type BaseValue = string | { id: string; label: string };

export interface SelectionRowProps<T extends BaseValue = BaseValue> {
  title: string;
  required?: boolean;
  descriptionEmpty: string; // e.g. "No folder selected."
  actionIcon?: LucideIcon;
  actionKind: ActionKind;
  onOpenDialog?: () => void; // required for 'open-tree'
  fileAccept?: string; // for 'open-file'
  fileMultiple?: boolean; // for 'open-file'
  values?: Array<T>; // selected items
  onRemove?: (idOrLabel: string) => void;
  onFilesChosen?: (files: FileList) => void; // for 'open-file'
  disabled?: boolean;
  icons?: LucideIcon; // icon to use for the node
  iconsClassName?: string; // additional classes for the icon
  actionTooltip?: ReactNode;
  autoFocus?: boolean;
}

export const SelectionRow: FC<SelectionRowProps> = ({
  title,
  required,
  descriptionEmpty,
  actionIcon: ActionIcon,
  actionKind,
  onOpenDialog,
  fileAccept,
  fileMultiple,
  values = [],
  onRemove,
  onFilesChosen,
  disabled,
  icons,
  iconsClassName,
  actionTooltip,
  autoFocus = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleAction = () => {
    if (disabled) {
      return;
    }
    if (actionKind === 'open-file') {
      inputRef.current?.click();
    } else {
      onOpenDialog?.();
    }
  };

  return (
    <div className="flex flex-col gap-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label variant={LabelVariants.INSPECTOR_SECTION} className={cn('text-text-gray', { 'opacity-25': disabled })}>
          {title}
          {required && (
            <span className="text-status-failure" data-cy={`${kebabCase(title)}-required-indicator`}>
              *
            </span>
          )}
        </Label>
        {ActionIcon && (
          <Button
            type="button"
            onClick={handleAction}
            disabled={disabled}
            variant="main-toolbar"
            size="secondary-toolbar"
            className="p-0 hover:[&>svg]:stroke-icon-base"
            aria-label={`${title} action`}
            data-cy={`${kebabCase(title)}-action-button`}
            tooltip={actionTooltip}
            icon={ActionIcon}
            iconClassName="icon-5"
          />
        )}
        {actionKind === 'open-file' && (
          <input
            autoFocus={autoFocus}
            ref={inputRef}
            type="file"
            accept={fileAccept}
            multiple={Boolean(fileMultiple)}
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (files && onFilesChosen) {
                onFilesChosen(files);
              }
              // Reset so same file can be chosen again
              e.currentTarget.value = '';
            }}
            disabled={disabled}
            data-cy={`${kebabCase(title)}-file-input`}
          />
        )}
      </div>

      {values.length === 0 ? (
        <div
          className={cn('flex h-6 items-center text-xs italic text-text-gray-alternate', { 'opacity-25': disabled })}
          data-cy={`${kebabCase(title)}-empty-description`}
        >
          {descriptionEmpty}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {values.map((v) => {
            const key = typeof v === 'string' ? v : (v.id ?? v.label);
            const label = typeof v === 'string' ? v : v.label;

            return (
              <Node
                key={key}
                path={label}
                icon={icons}
                iconClassName={iconsClassName}
                disabled={disabled}
                onRemove={onRemove ? () => onRemove(key) : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
